import React, { PureComponent } from "react"

import { Col, Form, Spinner } from "react-bootstrap"
import { Typeahead } from "react-bootstrap-typeahead"
import { toast } from "react-toastify"
import { BehaviorSubject, concat, of, Subject, Subscription } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"

import CHSServices from "./CHS/CHSServices"
import CHSTests from "./CHS/CHSTests"
import { SelectServiceProps } from "./SelectService"
import SelectServiceTogglePanel from "./SelectServiceTogglePanel"
import { ServiceByCode } from "../../../api/Administration/Model/Service"
import { ServiceTypeCode } from "../../../api/Administration/Model/ServiceType"
import LabAccountsGetObservable from "../../../api/LabService/LabAccountsGet"
import LabAccount from "../../../api/LabService/LabServiceModel/LabAccount"
import TestToBePerform from "../../../api/LabService/LabServiceModel/TestToBePerform"
import TestToBePerformWithCompanyName from "../../../api/LabService/LabServiceModel/TestToBePerformWithCompanyName"
import TestToBePerformGetObservable from "../../../api/LabService/TestToBePerformGet"
import { APIError } from "../../../api/ObservableFromFetch"
import FormatError from "../../../formatters/FormatError"
import { EmptyServiceData, ServiceDataModel } from "../OrderModels"
import { ServiceCode } from "../../../api/LookupCodes/ServiceCode"

interface SelectCHSProps extends SelectServiceProps {
  company?: number
  location?: number
  services: ServiceDataModel[]
}

interface SelectCHSTestState {
  serviceData: ServiceDataModel
  labAccountsLoading: boolean
  labAccounts: LabAccount[]
  testsLoading: boolean
  availableTests: TestToBePerform[]
  labAccountCompanyName?: string
}

export default class SelectCHS extends PureComponent<SelectCHSProps, SelectCHSTestState> {
  defaultService = {
    ...EmptyServiceData,
    serviceTypeCode: ServiceTypeCode.CHS,
    serviceCode: ServiceCode.None,
    selectedTests: [],
    selectedServices: [],
  }

  state: SelectCHSTestState = {
    serviceData: { ...this.defaultService },
    labAccountsLoading: false,
    labAccounts: [],
    testsLoading: false,
    availableTests: [],
  }

  subscriptions = new Subscription()

  company$ = new BehaviorSubject<number>(0)

  labAccount$ = new Subject<LabAccount>()

  componentDidMount() {
    const { company, location } = this.props

    this.subscriptions.add(
      this.company$
        .pipe(
          switchMap((companyId: number) =>
            !companyId
              ? of({ isLoading: false })
              : concat(
                  of({ isLoading: true }),
                  LabAccountsGetObservable({
                    SearchFor: "",
                    SearchBy: "Account Name",
                    CompanyID: companyId,
                    RegionID: 0,
                    LocationID: location || 0,
                    DOT: false,
                    IsMarketPlace: true,
                  }).pipe(
                    map((data) => ({ isLoading: false, result: data })),
                    catchError((error) => of({ isLoading: false, error }))
                  )
                )
          )
        )
        .subscribe((data: { isLoading: boolean; result?: LabAccount[]; error?: Error | APIError }) => {
          if (data.error) {
            toast.error(FormatError(data.error))
          }
          this.setState({ labAccountsLoading: data.isLoading, labAccounts: data.result || [] })
          this.labAccount$.next(undefined)
        })
    )

    this.subscriptions.add(
      this.labAccount$.subscribe((selected) =>
        this.setState((prev) => ({
          ...prev,
          serviceData: {
            ...prev.serviceData,
            labAccount: selected?.LabCode || "",
            labAccountObj: selected,
            availableTests: [],
            testToPerform: "",
            testToPerformObj: undefined,
            labAccountCompanyName: undefined,
          },
        }))
      )
    )

    this.subscriptions.add(
      this.labAccount$
        .pipe(
          switchMap((selected: LabAccount) =>
            !selected
              ? of({ isLoading: false })
              : concat(
                  of({ isLoading: true }),
                  TestToBePerformGetObservable({
                    LabAccountNumber: selected.LabAccount,
                    VendorID: selected.LabClientMemberId,
                    IsDOT: false,
                    IsPoct: false,
                    IsMarketPlace: true,
                  }).pipe(
                    map((data) => ({ isLoading: false, result: data })),
                    catchError((error) => of({ isLoading: false, error }))
                  )
                )
          )
        )
        .subscribe(
          (data: { isLoading: boolean; result?: TestToBePerformWithCompanyName; error?: Error | APIError }) => {
            if (data.error) {
              toast.error(FormatError(data.error))
            }
            this.setState((prev) => ({
              ...prev,
              testsLoading: data.isLoading,
              availableTests: data.result?.Panels || [],
              labAccountCompanyName: data.result?.CompanyName,
              serviceData: {
                ...prev.serviceData,
                testToPerform: "",
                testToPerformObj: undefined,
                selectedTests: [],
                selectedServices: [],
              },
            }))
          }
        )
    )

    if (company) {
      this.company$.next(company)
    }
  }

  componentDidUpdate(prevProps: SelectCHSProps) {
    const { company, location } = this.props

    if (company && (company !== prevProps.company || (location && location !== prevProps.location))) {
      this.company$.next(company)
    }
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  onChangeLab = (lab: LabAccount) => this.labAccount$.next(lab)

  onSelectService = (serviceCode: ServiceCode, add: boolean) => {
    const { orderableServices } = this.props
    const service = ServiceByCode(orderableServices, serviceCode)

    this.setState((prev) => {
      const { selectedServices } = prev.serviceData

      if (add && !selectedServices.find((item) => item.ServiceCode === service.ServiceCode)) {
        return {
          ...prev,
          serviceData: {
            ...prev.serviceData,
            selectedServices: [...prev.serviceData.selectedServices, service],
          },
        }
      }
      return {
        ...prev,
        serviceData: {
          ...prev.serviceData,
          selectedServices: prev.serviceData.selectedServices.filter((s) => s.ServiceCode !== service.ServiceCode),
        },
      }
    })
  }

  onServiceAdd = () => {
    const { onAdd } = this.props
    const { serviceData } = this.state

    onAdd(serviceData)
    this.setInitialState()
  }

  onPanelClose = () => this.setInitialState()

  onClinicalTestToBePerformedChange = (selected: TestToBePerform[]) => {
    this.setState((prev) => ({
      ...prev,
      serviceData: {
        ...prev.serviceData,
        testToPerform: selected.map((test) => test.Code).join(", ") || "",
        selectedTests: selected,
      },
    }))
  }

  setInitialState = () => this.setState({ serviceData: this.defaultService })

  isValid = (): boolean => {
    const { denyAddService } = this.props
    const {
      serviceData: { labAccountObj, selectedTests, selectedServices },
    } = this.state

    // TODO: Should returns false if all services are disabled

    return !denyAddService && !!labAccountObj && selectedTests.length > 0 && selectedServices.length > 0
  }

  render() {
    const {
      services,
      disabled,
      orderableServiceType,
      labAccountNumber,
      orderableServices,
      orderableAccountServices,
      canAddDOT,
      canAddNonDOT,
      notMarketplaceServicesCount,
      marketplaceServicesCount,
    } = this.props

    const {
      labAccounts,
      labAccountsLoading,
      labAccountCompanyName,
      availableTests,
      testsLoading,
      serviceData,
      serviceData: { labAccountObj, selectedTests, selectedServices },
    } = this.state

    const showPanels = labAccountObj && availableTests.length > 0
    const showNoServicesFound = !labAccountsLoading && labAccountObj && !testsLoading && availableTests.length === 0

    return (
      <SelectServiceTogglePanel
        title={orderableServiceType.ServiceTypeName || "Clinical Health Services"}
        data={serviceData}
        collapsibleId="CHSCollapsePanel"
        disabled={disabled}
        onSubmit={this.onServiceAdd}
        onValidate={this.isValid}
        onClose={this.onPanelClose}
      >
        <Form.Row>
          <Col xs="12">
            <Form.Group>
              <Form.Label className="font-weight-bold required">LAB ACCOUNT</Form.Label>
              <Typeahead
                id="labAccountsTypeahead"
                labelKey="Description"
                filterBy={(option, props) => {
                  return (
                    props.selected.length > 0 ||
                    option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                  )
                }}
                onChange={(selected: LabAccount[]) => this.onChangeLab(selected[0])}
                isInvalid={!labAccountObj}
                options={labAccounts}
                disabled={!!labAccountNumber}
                placeholder="Enter the Lab Account name"
                selected={labAccountObj ? [labAccountObj] : []}
              />
            </Form.Group>
          </Col>
        </Form.Row>
        {labAccountCompanyName && (
          <Form.Row>
            <Col xs="12" className="font-weight-bold form-group mb-2">
              {labAccountCompanyName}
            </Col>
          </Form.Row>
        )}
        {showNoServicesFound && (
          <Form.Row>
            <Col xs="12" className="error-message">
              No services found for lab account
            </Col>
          </Form.Row>
        )}
        {testsLoading && (
          <Form.Row>
            <Col className="text-center">
              <Spinner animation="border" variant="secondary" />
            </Col>
          </Form.Row>
        )}
        {showPanels && (
          <Form.Row>
            <Col xs="12">
              <CHSTests
                availableTests={availableTests}
                selectedTests={selectedTests}
                disabled={testsLoading}
                onChange={this.onClinicalTestToBePerformedChange}
              />
            </Col>
          </Form.Row>
        )}
        {orderableServices.length > 0 && (
          <Form.Row className="px-2">
            <Col xs="12">
              <CHSServices
                services={services}
                orderableServices={orderableServices}
                selectedServices={selectedServices}
                accountServices={orderableAccountServices}
                canAddDOT={canAddDOT}
                canAddNonDOT={canAddNonDOT}
                canAddMarketplace={notMarketplaceServicesCount === 0}
                canAddNotMarketplace={marketplaceServicesCount === 0}
                onSelectService={this.onSelectService}
              />
            </Col>
          </Form.Row>
        )}
      </SelectServiceTogglePanel>
    )
  }

  static defaultProps = {
    company: 0,
    location: 0,
  }
}
